import "./MuiClassNameSetup";
import React, { useEffect, useState } from "react";
import {
  ACTION,
  CATEGORY,
  evt,
  initAnalytics,
  setUserForAnalytics,
} from "fe-common/analytics/analytics";
import { gqlClient } from "fe-common/graphql/webClient";
import { ClinicDao, WebDao } from "fe-common/graphql/dao";
import { DaoProvider } from "fe-web-components/diseaseview/DaoContext";
import {
  SearchQueriesContext,
  useSearchQueriesContext,
} from "./context/searchQuery";
import { ProviderDiagnosticAgentNavigation } from "./context/diagnosticAgent";
import {
  DiagnosticsContext,
  useDiagnosticsContext,
} from "fe-web-components/diagnostics/diagnosticsContext";
import {
  ProviderDiagnosticAgentState,
  ProviderSuggestionsState,
} from "./context/diagnosticAgent";
import { KahunRouter } from "./kahunRouter";
import { isProduction } from "fe-common/utils/envGetter";
import { addSpreadsheetToGapiClient } from "fe-web-components/diagnostics/debugsheetExporter";
import {
  getCurrentUserAttr,
  getCurrentUserEmail,
  isValidSession,
  UserAttrs,
} from "@kahunmedical/user-management";
import { getVersion } from "./VersionInfo";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { SessionContext } from "fe-web-components/userSession/userSession";
import {
  readFromGlobalStorage,
  writeToGlobalStorage,
} from "fe-web-components/userStorage/globalUserStorage";
import { ClinicDaoProvider } from "fe-web-components/clinic/ClinicContext";
import { createGenerateClassName, StylesProvider } from "@mui/styles";
import {
  ConfigEnvironmentSettingsAccess,
  MergedConfigurationSettings,
} from "@kahunmedical/common-configuration";
import { getV2ClinicSettings } from "fe-web-components/settings/common/getV2ClinicSettings";
import { PageTitleContext } from "./context/pageTitleContext";
import { LocaleContextProvider } from "fe-common/locale/localeContext";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";

const useQueryParams = () => new URLSearchParams(window.location.search);
initAnalytics("3eab33fd36cc7086e96f400904729345", "G-8WRWVTT7DZ");

if (!isProduction()) {
  addSpreadsheetToGapiClient();
}
const WEB_DAO = new WebDao(gqlClient);
const CLINIC_DAO = new ClinicDao(gqlClient);

const generateClassName = createGenerateClassName({
  productionPrefix: "kahun",
  seed: "agent",
  disableGlobal: true,
});

const startMixPanelReportingFromOneOf = [
  { category: CATEGORY.AGENT_MAIN, action: ACTION.SELECT_CHIEF_COMPLAINT },
  { category: CATEGORY.AGENT_WIZARD, action: ACTION.WIZARD_COMPLETE },
  { category: CATEGORY.AGENT, action: ACTION.RESTORE_CASE },
];

function App() {
  const contextStore = useSearchQueriesContext();
  const { setAgreeOnTerms } = contextStore;
  const diagnosticsStore = useDiagnosticsContext();
  const queryParams = useQueryParams();
  const [loading, setLoading] = useState(true);
  const [pageTitle, setPageTitle] = useState("Kahun Clinical Intake");
  const [pageIcon, setPageIcon] = useState("favicon.ico?v=2");
  const clinicId = queryParams.get("clinic");
  const partner = queryParams.get("pid");

  useEffect(() => {
    (async () => {
      const isValid = await isValidSession();
      if (isValid) {
        const email = await getCurrentUserEmail();
        setUserForAnalytics(email, partner ? { partner } : {});
        setAgreeOnTerms(true);
        // Set speciality according to user
        getCurrentUserAttr(UserAttrs.SPECIALITY).then((speciality) => {
          writeToGlobalStorage(UserStorageKey.Speciality, speciality);
        });
      } else {
        const wizardCompleted = readFromGlobalStorage(
          UserStorageKey.WizardCompleteCount,
        );
        setUserForAnalytics(
          null,
          partner ? { partner } : {},
          wizardCompleted ? null : startMixPanelReportingFromOneOf,
        );
      }
      evt({
        category: CATEGORY.AGENT,
        action: ACTION.SESSION_INIT,
        label: getVersion(),
        sendImmediately: true,
      });
    })();
  }, [queryParams, setAgreeOnTerms, partner]);

  useEffect(() => {
    if (clinicId) {
      (async () => {
        const [clinicConfig, envSettings] = await Promise.all([
          await CLINIC_DAO.getClinicConfig(clinicId),
          new ConfigEnvironmentSettingsAccess().getSettings("prod"),
        ]);

        const mergedSettings = new MergedConfigurationSettings(
          {},
          clinicConfig?.mergedConfiguration || [
            clinicConfig?.name,
            getV2ClinicSettings(clinicConfig?.config),
          ],
          envSettings,
        );
        WEB_DAO.updateSettingsObject(mergedSettings);
        setLoading(false);
      })();
    } else {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    if (document?.title && pageTitle !== "") {
      document.title = pageTitle;
    }
    if (pageIcon) {
      const link = document.createElement("link"),
        oldLink = document.getElementById("dynamic-favicon");
      link.id = "dynamic-favicon";
      link.rel = "shortcut icon";
      link.href = `/${pageIcon}`;
      if (oldLink) {
        document.head.removeChild(oldLink);
      }
      document.head.appendChild(link);
    }
  }, [pageTitle, pageIcon]);

  if (loading) return <ThreeDotsLoader />;

  return (
    <PageTitleContext.Provider value={{ setPageTitle, setPageIcon }}>
      <StylesProvider generateClassName={generateClassName}>
        <SessionContext>
          <SearchQueriesContext.Provider value={contextStore}>
            <DaoProvider value={WEB_DAO}>
              <ClinicDaoProvider value={CLINIC_DAO}>
                <LocaleContextProvider baseLocale={"en"} partnerId={partner}>
                  <DiagnosticsContext.Provider value={diagnosticsStore}>
                    <ProviderDiagnosticAgentState value={null}>
                      <ProviderSuggestionsState>
                        <ProviderDiagnosticAgentNavigation>
                          <KahunRouter />
                        </ProviderDiagnosticAgentNavigation>
                      </ProviderSuggestionsState>
                    </ProviderDiagnosticAgentState>
                  </DiagnosticsContext.Provider>
                </LocaleContextProvider>
              </ClinicDaoProvider>
            </DaoProvider>
          </SearchQueriesContext.Provider>
        </SessionContext>
      </StylesProvider>
    </PageTitleContext.Provider>
  );
}

export default App;
